/**
 * The address rule — the portal's copy of the one the server applies to every address it stores.
 *
 * **Paired with the route schemas' `format: 'email'`** in `backend/src/http/routes/admin.ts`: a new
 * user, *Cambiar el correo*, and a new company's first administrator (`admin.email`). Fastify checks
 * that keyword with `ajv-formats`, which `@fastify/ajv-compiler` adds with its defaults — the *full*
 * mode — and the expression below is that mode's `email`, character for character (ajv-formats
 * 3.0.1, `dist/formats.js`).
 *
 * **It must refuse nothing the server takes.** A portal stricter than the server leaves an
 * administrator no way at all to invite a person whose address it dislikes. The other direction
 * only costs a round trip: `invalidEmailMessage` (`app/errors.ts`) puts the server's refusal on the
 * same field with the same sentence. So if the two ever part, this copy must be the looser.
 * `src/test/email-address.test.ts` holds the server's own verdicts.
 *
 * A copy is needed at all because none of the dialogs is a `<form>`, so the browser's `type="email"`
 * check never runs — and that is a different rule anyway (WHATWG's, which takes `pmoreno@empresa`).
 * The length is not checked: the routes cap it at 320 characters, or 254 for a change of address,
 * and that refusal comes back as the same sentence on the same field.
 */

import type { TFunction } from 'i18next'

const EMAIL_ADDRESS =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i

/** Whether the server would take `value` as an address. */
export function isEmailAddress(value: string): boolean {
  return EMAIL_ADDRESS.test(value)
}

/** react-hook-form's rules for an address field: required, and an address the server would take. */
export function emailAddressRules(t: TFunction) {
  return {
    required: t('errors.required'),
    validate: (value: string) => isEmailAddress(value) || t('errors.invalidEmail'),
  }
}
